import React from "react";
import { Box, Modal } from "@mui/material";
import { useIntl } from "react-intl";
import { AutoCompleteDropdown } from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import Button, {
  COLOR,
  IconButton,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import { getUserAvatarIcon } from "features/metadesk/utils";
import {
  StyledExcludeUserContainer,
  StyledTrackingModalContainer,
  StyledTrackingSettingBody,
  StyledTrackingSettingBodyContainer,
  StyledTrackingSettingFooter,
  StyledTrackingSettingHeader,
} from "./KeywordTrackingSettingModal.styles";

function KeywordTrackingSettingModal({
  isOpen,
  setIsOpen,
  theme,
  excludedWords,
  excludedUsers,
  handleChangeWordFilter,
  handleCreateNewWordFilter,
  handleCancel,
  manageUserFilter,
  manageKeywordFilter,
  dateRange,
  isError,
  type,
}) {
  const { formatMessage } = useIntl();

  if (!isOpen) return null;

  return (
    <>
      <Modal className="modal" open={isOpen}>
        <StyledTrackingModalContainer>
          <StyledTrackingSettingHeader>
            <div className="modalHeader">
              {formatMessage({
                id: "communityAnalysis.keywordTrackerSetting.title",
              })}
            </div>
            <IconButton
              style={{ float: "right", padding: 0, color: "#555555" }}
              onClick={() => setIsOpen(false)}>
              <i className="meta-crm-icon-ic_cancel font-size-16" />
            </IconButton>
          </StyledTrackingSettingHeader>
          <StyledTrackingSettingBody>
            <StyledTrackingSettingBodyContainer m={"24px 0px 24px"}>
              {formatMessage({
                id: "communityAnalysis.keywordTrackerSetting.description",
              })}
            </StyledTrackingSettingBodyContainer>
            <StyledTrackingSettingBodyContainer mb={"24px"}>
              <div style={{ fontSize: 16, color: "#555555" }}>
                {formatMessage({
                  id: "communityAnalysis.keywordTrackerSetting.keywordFilter.subtitle",
                })}
              </div>
              <div style={{ padding: "4px 0px 8px", fontSize: 14 }}>
                {formatMessage({
                  id: "communityAnalysis.keywordTrackerSetting.keywordFilter.description",
                })}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: !isError
                    ? "#555555"
                    : `${theme.customColors.pink[600]}`,
                  paddingBottom: 4,
                }}>
                {formatMessage({
                  id: "communityAnalysis.keywordTrackerSetting.keywordFilter.labelName",
                })}
              </div>
              <div>
                <AutoCompleteDropdown
                  multiple
                  isTag
                  placeholder={formatMessage({
                    id: "communityAnalysis.keywordTrackerSetting.keywordFilter.placeholder",
                  })}
                  value={excludedWords}
                  onChange={handleChangeWordFilter}
                  open={false}
                  options={[]}
                  onEnter={(value) => {
                    handleCreateNewWordFilter(value);
                  }}
                  isInputFieldBehaviour={true}
                  errors={
                    isError
                      ? formatMessage(
                          { id: "global.error.lengthInterval" },
                          { min: 3, max: 20 }
                        )
                      : ""
                  }
                  errorTextAbsolute={false}
                  isShowBorderError={true}
                />
              </div>
              <div
                style={{
                  fontSize: 14,
                  padding: "4px 0px 8px",
                }}>
                {formatMessage({
                  id: "communityAnalysis.keywordTrackerSetting.keywordFilter.explanation",
                })}
              </div>
            </StyledTrackingSettingBodyContainer>
            <StyledTrackingSettingBodyContainer mb={"24px"}>
              <div style={{ fontSize: 16, color: "#555555" }}>
                {formatMessage({
                  id: "communityAnalysis.keywordTrackerSetting.accountFilter.subtitle",
                })}
              </div>
              <div style={{ padding: "4px 0px 8px", fontSize: 14 }}>
                {formatMessage({
                  id: "communityAnalysis.keywordTrackerSetting.accountFilter.description",
                })}
              </div>
              {excludedUsers.length > 0 ? (
                excludedUsers.map((user) => (
                  <StyledExcludeUserContainer mb={"4px"}>
                    <Box className="startRow">
                      <img
                        src={require(`assets/img/${getUserAvatarIcon(
                          user._id,
                          theme
                        )}`)}
                        className="icon"
                        style={{
                          borderRadius: 20,
                          marginRight: 8,
                          height: 20,
                          width: 20,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}>
                      <div
                        style={{
                          fontSize: 14,
                          color: theme.customColors.grey[700],
                        }}>
                        {user.userHandle}
                      </div>
                      <Button
                        variant={VARIANT.TEXT}
                        style={{
                          padding: "0px",
                          height: "fit-content",
                          minWidth: "fit-content",
                        }}
                        onClick={async () => {
                          setIsOpen(false);
                          await manageUserFilter({
                            platform: type,
                            userHandle: user.userHandle,
                            option: "include",
                            startDate: dateRange[0],
                            endDate: dateRange[1],
                          });
                        }}>
                        {formatMessage({ id: "global.btn.reinstate" })}
                      </Button>
                    </Box>
                  </StyledExcludeUserContainer>
                ))
              ) : (
                <StyledExcludeUserContainer
                  style={{
                    border: `1px dashed ${theme.customColors.grey[300]}`,
                  }}
                  mb={"4px"}>
                  <div
                    style={{
                      fontSize: 14,
                      color: theme.customColors.grey[500],
                    }}>
                    {"No data"}
                  </div>
                </StyledExcludeUserContainer>
              )}
            </StyledTrackingSettingBodyContainer>
          </StyledTrackingSettingBody>
          <StyledTrackingSettingFooter>
            <Button
              style={{ width: 120, height: 36 }}
              color={COLOR.SECONDARY}
              variant={VARIANT.OUTLINED}
              onClick={() => {
                setIsOpen(false);
                handleCancel();
              }}>
              {formatMessage({ id: "global.btn.cancel" })}
            </Button>
            <Button
              style={{ width: 120, height: 36 }}
              disabled={isError}
              color={COLOR.SECONDARY}
              onClick={async () => {
                setIsOpen(false);
                await manageKeywordFilter({
                  platform: type,
                  startDate: dateRange[0],
                  endDate: dateRange[1],
                });
              }}>
              {formatMessage({ id: "global.btn.save" })}
            </Button>
          </StyledTrackingSettingFooter>
        </StyledTrackingModalContainer>
      </Modal>
    </>
  );
}

export default KeywordTrackingSettingModal;
